import React from 'react';
import {
    X, BarChart3, Truck, ClipboardList, Edit, Move, FileText, History, Settings,
    User, Zap, Shield, Bug, Grid3x3, Activity, Radio, Flame,
    TrendingUp, ShoppingCart, MapPin, Wrench, Database, Calculator, Package, ShoppingBag, LayoutDashboard, PlayCircle,
    Box, Clock, ScrollText, FolderTree, Link2, Users, Tag, ScanLine, ArrowRightLeft, WarehouseIcon, Check, Layers, Palette, Globe,
    Combine
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useWarehouse } from '../../contexts/WarehouseContext';
import { useTranslation } from '../../contexts/I18nProvider';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { OPERATOR_MODULES } from '../../constants/operatorModules';

interface SideMenuProps {
    isOpen: boolean;
    onClose: () => void;
    currentModule: string;
    onModuleChange: (moduleId: string) => void;
}

interface MenuItem {
    id: string;
    name: string;
    icon: any;
    moduleId?: string;
    disabled?: boolean;
}

interface MenuSection {
    id: string;
    name: string;
    items: MenuItem[];
}

interface MenuGroup {
    id: string;
    name: string;
    sections: MenuSection[];
}

export const SideMenu: React.FC<SideMenuProps> = ({ isOpen, onClose, currentModule, onModuleChange }) => {
    const { user } = useAuth();
    const { currentWarehouse, warehouses, setCurrentWarehouse } = useWarehouse();
    const { t } = useTranslation();
    const { isModuleAccessible } = useModuleAccess();
    const isAdvancedWarehouse = warehouses.find(w => w.warehouseID === currentWarehouse)?.useAWYesNo ?? false;

    // Operatørmoduler — autoritativ liste delt med Dashboard og ManagerDashboard
    const operatorModules: MenuItem[] = OPERATOR_MODULES
        .filter(m => !m.requiresAdvancedWarehouse || isAdvancedWarehouse)
        .map(m => ({
            id: m.id,
            name: t(m.nameKey, {}, m.nameFallback),
            icon: m.icon,
            moduleId: m.moduleId
        }));

    // Lagersjefens moduler - strukturert i grupper som ManagerDashboard
    const managerGroups: MenuGroup[] = [
        {
            id: 'plukk-pakk',
            name: 'Plukk & Pakk',
            sections: [
                {
                    id: 'nye-ordre',
                    name: 'Nye Ordre',
                    items: [
                        { id: 'new-orders', name: 'Ordre', icon: ShoppingCart, moduleId: 'new-orders' },
                        { id: 'order-lines', name: 'Ordrelinje', icon: FileText, moduleId: 'order-lines' }
                    ]
                },
                {
                    id: 'plukklister',
                    name: 'Plukklister',
                    items: [
                        { id: 'new-picking-lists', name: 'Nye plukklister', icon: ClipboardList, moduleId: 'new-picking-lists' },
                        { id: 'working-picking-lists', name: 'Plukklister under arbeid', icon: Package, moduleId: 'working-picking-lists' }
                    ]
                },
                {
                    id: 'historikk-plukk',
                    name: 'Historikk',
                    items: [
                        { id: 'completed-picking-lists', name: 'Avsluttede plukklister', icon: ClipboardList, moduleId: 'completed-picking-lists' },
                        { id: 'order-history', name: 'Ordrehistorikk', icon: History, moduleId: 'order-history' },
                        { id: 'statistics', name: 'Statistikk', icon: BarChart3, moduleId: 'statistics' }
                    ]
                }
            ]
        },
        {
            id: 'varemottak',
            name: 'Varemottak',
            sections: [
                {
                    id: 'bestilling',
                    name: 'Bestilling',
                    items: [
                        { id: 'working-orders', name: 'Administrer bestillinger', icon: FileText, moduleId: 'working-orders' },
                        { id: 'bestillingslinje', name: 'Bestillingslinje', icon: FileText, moduleId: 'purchase-order-lines' }
                    ]
                },
                {
                    id: 'historikk-varemottak',
                    name: 'Historikk',
                    items: [
                        { id: 'historikk-bestilling', name: 'Bestillingshistorikk', icon: History, moduleId: 'purchase-order-history' }
                    ]
                }
            ]
        },
        {
            id: 'lager',
            name: 'Lager',
            sections: [
                {
                    id: 'transaksjoner',
                    name: 'Transaksjoner',
                    items: [
                        { id: 'vare-lokasjonssaldo', name: 'Vare og lokasjonssaldo', icon: TrendingUp, moduleId: 'item-location-balance' },
                        { id: 'transaksjonshistorikk', name: 'Transaksjonshistorikk', icon: History, moduleId: 'transaction-history' },
                        { id: 'reservasjoner', name: 'Reservasjoner', icon: ClipboardList, moduleId: 'reservations' }
                    ]
                },
                {
                    id: 'vareplassering',
                    name: 'Vareplassering',
                    items: [
                        { id: 'grunnlag', name: 'Grunnlag', icon: Database, moduleId: 'bin-placement-grunnlag' },
                        { id: 'plassering-ordre', name: 'Ordre', icon: ShoppingCart, moduleId: 'put-away' },
                        { id: 'plassering-ordrelinjer', name: 'Ordrelinjer', icon: FileText, moduleId: 'put-away' }
                    ]
                },
                {
                    id: 'verktoy',
                    name: 'Verktøy',
                    items: [
                        { id: 'oprett-utlegg', name: 'Opprett utlegg', icon: Wrench, moduleId: 'create-putaway' },
                        { id: 'oprett-pafylling', name: 'Opprett påfylling', icon: Wrench, moduleId: 'create-replenishment' },
                        { id: 'konsolidering-planlegging', name: 'Konsolidering', icon: Combine, moduleId: 'consolidation-planning' }
                    ]
                },
                {
                    id: 'grunndata',
                    name: 'Grunndata',
                    items: [
                        { id: 'vedlikehold-lager', name: 'Vedlikehold Lager', icon: Wrench, moduleId: 'warehouse-maintenance' },
                        { id: 'rack-builder', name: 'Reolbygger', icon: Grid3x3, moduleId: 'rack-builder' },
                        { id: 'location-heatmap', name: 'Lokasjon HeatMap', icon: Flame, moduleId: 'location-heatmap' },
                        { id: 'move-processes', name: 'Flytteplaner', icon: ClipboardList, moduleId: 'move-processes' },
                        { id: 'rack-builder-live', name: 'Live Aktivitet', icon: Radio, moduleId: 'rack-builder-live' },
                        { id: 'realtime-stats', name: 'Sanntidsstatistikk', icon: Activity, moduleId: 'realtime-stats' },
                        { id: 'warehouse-layout-3d', name: '3D Lagervisning', icon: Box, moduleId: 'warehouse-layout-3d' }
                    ]
                },
                {
                    id: 'lagertelling',
                    name: 'Lagertelling',
                    items: [
                        { id: 'inventory-counting-dashboard', name: 'Telledashboard', icon: Calculator, moduleId: 'inventory-counting-dashboard' },
                        { id: 'counting-list-creation', name: 'Opprett tellelister', icon: ClipboardList, moduleId: 'counting-list-creation' }
                    ]
                }
            ]
        },
        {
            id: 'system',
            name: 'System',
            sections: [
                {
                    id: 'konfigurasjon',
                    name: 'Konfigurasjon',
                    items: [
                        { id: 'order-timeline', name: 'Ordretidslinje', icon: Clock, moduleId: 'order-timeline' },
                        { id: 'lagersted', name: 'Lagersted', icon: Database, moduleId: 'warehouse-management' },
                        { id: 'regler', name: 'Regler', icon: ScrollText, moduleId: 'system-rules' },
                        { id: 'regelgrupper', name: 'Regelgrupper', icon: FolderTree, moduleId: 'system-rule-groups' },
                        { id: 'regelsammensetning', name: 'Regelsammensetning', icon: Link2, moduleId: 'rule-composition' },
                        { id: 'sql-syntax', name: 'SQL Syntaks', icon: Database, moduleId: 'sql-syntax-admin' },
                        { id: 'profile-admin', name: 'Profiler', icon: Users, moduleId: 'profile-admin' },
                        { id: 'system-settings', name: 'Systeminnstillinger', icon: Settings, moduleId: 'system-settings' },
                        { id: 'reason-codes', name: 'Årsakskoder', icon: Tag, moduleId: 'reason-codes' },
                        { id: 'delivery-methods', name: 'Leveringsmåter', icon: Truck, moduleId: 'delivery-methods' },
                        { id: 'ta-consignor-mappings', name: 'Avsendere (TA)', icon: ArrowRightLeft, moduleId: 'ta-consignor-mappings' },
                        { id: 'pack-zones', name: 'Pakkesoner', icon: Package, moduleId: 'pack-zones' },
                        { id: 'loading-zones', name: 'Lastesoner', icon: Truck, moduleId: 'loading-zones' },
                        { id: 'filter-groups', name: 'Filtergrupper', icon: Layers, moduleId: 'filter-groups' },
                        { id: 'administration', name: 'Administrasjon', icon: Wrench, moduleId: 'administration' }
                    ]
                }
            ]
        }
    ];

    // Admin-spesifikke moduler - flat struktur
    const adminModules: MenuItem[] = [
        { id: 'dashboard', name: 'Dashboard', icon: BarChart3, moduleId: 'dashboard' },
        { id: 'manager-dashboard', name: 'Lagersjef Dashboard', icon: TrendingUp, moduleId: 'manager-dashboard' },
        { id: 'wms-dashboard', name: 'WMS Dashboard', icon: LayoutDashboard, moduleId: 'wms-dashboard' },
        { id: 'administration', name: 'Administrasjon', icon: Shield, moduleId: 'administration' },
        { id: 'bestillingssystem-admin', name: 'Bestillingssystem (admin)', icon: ShoppingCart, moduleId: 'bestillingssystem-admin' },
        { id: 'rapport-designer', name: 'Rapport-designer', icon: Palette, moduleId: 'rapport-designer' },
        // Fellesmaler: synlighet styres av moduleAccess.sharedReportDesigner (kun IPB-tenants)
        { id: 'shared-report-designer', name: 'Rapport-designer (fellesmaler)', icon: Globe, moduleId: 'shared-report-designer' },
        { id: 'reports', name: 'Rapporter', icon: BarChart3, moduleId: 'reports' },
        { id: 'fase4-test', name: 'Fase 4: Live Animasjoner', icon: Activity, moduleId: 'fase4-test' },
        { id: 'simulator', name: 'Warehouse Simulator', icon: PlayCircle, moduleId: 'simulator' },
        { id: 'settings', name: 'Innstillinger', icon: Settings, moduleId: 'settings' }
    ];

    const handleItemClick = (item: MenuItem) => {
        if (item.disabled || !item.moduleId) return;
        onModuleChange(item.moduleId);
        onClose();
    };

    // Render funksjon for flat liste (operatør og admin)
    const renderFlatList = (items: MenuItem[]) => {
        return items.map(item => {
            const Icon = item.icon;
            const isActive = currentModule === item.moduleId;
            const isDisabled = item.disabled || !item.moduleId;

            return (
                <button
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    disabled={isDisabled}
                    className={`w-full flex items-center gap-3 p-3 text-left rounded-lg transition-all ${
                        isDisabled
                            ? 'text-gray-400 cursor-not-allowed opacity-60'
                            : isActive
                                ? 'bg-blue-100 text-blue-700 shadow-sm'
                                : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                    <Icon size={18} />
                    <span className="text-sm">{item.name}</span>
                </button>
            );
        });
    };

    // Render funksjon for grupperte moduler (lagersjef)
    const renderGroupedList = (groups: MenuGroup[]) => {
        return groups.map((group) => (
            <div key={group.id} className="mb-4">
                {/* Hovedgruppe header */}
                <div className="px-3 py-2 mb-2">
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                        {group.name}
                    </h3>
                </div>

                {/* Sections og items */}
                <div className="space-y-3">
                    {group.sections.map((section) => (
                        <div key={section.id}>
                            {/* Section navn (undergruppering) */}
                            <div className="px-3 mb-1">
                                <p className="text-xs text-gray-500 font-medium">
                                    {section.name}
                                </p>
                            </div>

                            {/* Items i section */}
                            <div className="space-y-1">
                                {section.items.map(item => {
                                    const Icon = item.icon;
                                    const isActive = currentModule === item.moduleId;
                                    const isDisabled = item.disabled || !item.moduleId;

                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => handleItemClick(item)}
                                            disabled={isDisabled}
                                            className={`w-full flex items-center gap-3 p-2 pl-6 text-left rounded-lg transition-all ${
                                                isDisabled
                                                    ? 'text-gray-400 cursor-not-allowed opacity-60'
                                                    : isActive
                                                        ? 'bg-blue-100 text-blue-700 shadow-sm'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            <Icon size={16} />
                                            <span className="text-sm">{item.name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ));
    };

    const operatorGroupId = user?.operatorGroupId;

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
            <div className="fixed left-0 top-0 h-full w-80 bg-white z-50 transform transition-transform flex flex-col shadow-xl">
                <div className="p-4 border-b flex-shrink-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold">Compass</h2>
                            <p className="text-sm text-blue-100">Warehouse Management</p>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-20 rounded text-white">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Warehouse section - always visible when warehouses exist */}
                {warehouses.length > 0 && (
                    <div className="px-4 py-3 border-b bg-gray-50">
                        <div className="flex items-center gap-2 mb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <WarehouseIcon size={14} />
                            <span>{warehouses.length > 1 ? t('warehouse.selector.selectWarehouse') : t('warehouse.general.warehouse')}</span>
                        </div>
                        {warehouses.length === 1 ? (
                            /* Single warehouse: read-only display */
                            <div className="flex items-center gap-2 px-3 py-2 text-sm text-blue-700 bg-blue-50 rounded-lg font-medium">
                                <WarehouseIcon size={14} />
                                <span>{warehouses[0].warehouseDescr || warehouses[0].warehouseID}</span>
                            </div>
                        ) : (
                            /* Multiple warehouses: selectable list */
                            <div className="space-y-1">
                                <button
                                    onClick={() => { setCurrentWarehouse(null); onClose(); }}
                                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                                        currentWarehouse === null
                                            ? 'bg-blue-100 text-blue-700 font-medium'
                                            : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    <Check size={14} className={currentWarehouse === null ? 'text-blue-600' : 'invisible'} />
                                    {t('warehouse.selector.allWarehouses')}
                                </button>
                                {warehouses.map(w => (
                                    <button
                                        key={w.warehouseID}
                                        onClick={() => { setCurrentWarehouse(w.warehouseID); onClose(); }}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                                            currentWarehouse === w.warehouseID
                                                ? 'bg-blue-100 text-blue-700 font-medium'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <Check size={14} className={currentWarehouse === w.warehouseID ? 'text-blue-600' : 'invisible'} />
                                        <span>{w.warehouseDescr || w.warehouseID}</span>
                                        <span className="text-gray-400 ml-auto text-xs">{w.warehouseID}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <nav className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-2">
                        {/* Operatør (operatorGroupId === '1') - filtrert basert på modultilgang */}
                        {operatorGroupId === '1' && renderFlatList(operatorModules.filter(m => isModuleAccessible(m.moduleId || m.id) && (m.id !== 'capacity-registration' || isAdvancedWarehouse)))}

                        {/* Lagersjef (operatorGroupId === '2') */}
                        {operatorGroupId === '2' && (
                            <>
                                {renderGroupedList(managerGroups)}

                                {/* Separator */}
                                <div className="my-4 border-t border-gray-300 pt-4">
                                    <div className="px-3 py-2 mb-2">
                                        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                            Operatør
                                        </h3>
                                    </div>
                                </div>

                                {renderFlatList(operatorModules.filter(m => isModuleAccessible(m.moduleId || m.id) && (m.id !== 'capacity-registration' || isAdvancedWarehouse)))}
                            </>
                        )}

                        {/* Sysadmin (operatorGroupId === '3') */}
                        {operatorGroupId === '3' && (
                            <>
                                {renderGroupedList(managerGroups)}

                                {/* Separator - Operatør */}
                                <div className="my-4 border-t border-gray-300 pt-4">
                                    <div className="px-3 py-2 mb-2">
                                        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                            Operatør
                                        </h3>
                                    </div>
                                </div>

                                {renderFlatList(operatorModules.filter(m => isModuleAccessible(m.moduleId || m.id) && (m.id !== 'capacity-registration' || isAdvancedWarehouse)))}

                                {/* Separator - System Administrator */}
                                <div className="my-4 border-t border-gray-300 pt-4">
                                    <div className="px-3 py-2 mb-2">
                                        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                            System Administrator
                                        </h3>
                                    </div>
                                </div>

                                {renderFlatList(adminModules.filter(m => isModuleAccessible(m.moduleId || m.id)))}
                            </>
                        )}
                    </div>
                </nav>

                <div className="p-4 border-t flex-shrink-0 bg-gray-50">
                    <button
                        onClick={() => {
                            onModuleChange('profile');
                            onClose();
                        }}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                            <User size={20} className="text-white" />
                        </div>
                        <div className="text-left">
                            <div className="font-medium text-gray-800">{user?.operatorName}</div>
                            <div className="text-sm text-gray-500">Vis profil</div>
                        </div>
                    </button>
                </div>
            </div>
        </>
    );
};
